import { GameMode, MATCH_INTRO_TICKS, MatchPhase, TICK_RATE, type ArenaState } from "@arena/shared";
import { CHARACTERS, findCharacter } from "@arena/character-data";
import { TEAM_NAMES, fighterCss } from "../palette.ts";

const portraits = new Map<string, Promise<HTMLImageElement>>();

/** Shares the close-up portraits used by champion selection. Source sizes vary. */
function portrait(id: string): Promise<HTMLImageElement> {
  let pending = portraits.get(id);
  if (!pending) {
    pending = new Promise((resolve,reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = `/proto2d/portraits/closeups/${id}.png`;
    });
    portraits.set(id,pending);
  }
  return pending;
}

export class MatchIntro {
  private readonly mapRoot = document.createElement("section");
  private readonly mapTitle = document.createElement("h2");
  private readonly mapCaption = document.createElement("p");
  /** The map card while the arena loads outside a match (training). */
  private loadingArena = false;
  private readonly root = document.createElement("section");
  private readonly mode = document.createElement("p");
  private readonly roster = document.createElement("div");
  private readonly remaining = document.createElement("span");
  private readonly progress = document.createElement("i");
  private rendered = "";

  constructor() {
    // Warm the portraits from the home screen, before a match can start.
    for (const champion of CHARACTERS) void portrait(champion.id).catch(()=>{});
    this.root.id = "match-intro";
    this.root.className = "match-intro";
    this.root.hidden = true;
    this.root.setAttribute("aria-label","Presentazione giocatori");
    const header = document.createElement("header");
    const kicker = document.createElement("p");
    kicker.className = "intro-kicker";
    kicker.textContent = "ARENA CROSSOVER";
    const title = document.createElement("h2");
    title.textContent = "I combattenti";
    this.mode.className = "intro-mode";
    header.append(kicker,title,this.mode);
    this.roster.className = "intro-roster";
    const footer = document.createElement("footer");
    const track = document.createElement("div");
    track.className = "intro-progress";
    track.append(this.progress);
    footer.append(this.remaining,track);
    this.root.append(header,this.roster,footer);
    this.mapRoot.className = "map-intro";
    this.mapRoot.hidden = true;
    this.mapRoot.setAttribute("aria-label", "Presentazione mappa");
    const caption = this.mapCaption;
    caption.textContent = "PROSSIMA ARENA";
    const emblem = document.createElement("div");
    emblem.className = "map-emblem";
    emblem.textContent = "Ⅲ";
    this.mapRoot.append(caption, emblem, this.mapTitle);
    document.body.append(this.mapRoot, this.root);
  }

  /** Shows the map card while the arena is still loading (entering training), until `on` is false. */
  loading(on: boolean): void {
    this.loadingArena = on;
  }

  clear(): void {
    this.root.hidden = true;
    this.mapRoot.hidden = true;
    document.body.classList.remove("pre-match");
    this.roster.replaceChildren();
    this.rendered = "";
  }

  update(state: ArenaState | null, localId = "", arenaName = ""): void {
    // Also warm portraits for any newly available champion while in the lobby.
    if (state?.phase === MatchPhase.Lobby) {
      for (const p of state.players.values()) if (!portraits.has(p.characterId)) void portrait(p.characterId).catch(()=>{});
    }
    const mapIntro = state?.phase === MatchPhase.MapIntro;
    this.mapRoot.hidden = !mapIntro && !this.loadingArena;
    this.mapCaption.textContent = mapIntro ? "PROSSIMA ARENA" : "ALLENAMENTO · CARICAMENTO";
    this.mapTitle.textContent = arenaName;
    document.body.classList.toggle("pre-match", state?.phase === MatchPhase.MapIntro || state?.phase === MatchPhase.Intro);
    const visible = state?.phase === MatchPhase.Intro;
    this.root.hidden = !visible;
    if (!state || !visible) return;
    const players = [...state.players.entries()].sort((a,b)=>a[1].team-b[1].team || a[1].color-b[1].color);
    const key = JSON.stringify([state.matchId,state.settings.gameMode,state.settings.teams,players.map(([id,p])=>[id,p.name,p.characterId,p.team,p.connected])]);
    if (key !== this.rendered) {
      this.rendered = key;
      this.mode.textContent = `${state.settings.gameMode === GameMode.KO ? "ARENA KO" : "SLAM"} · ${state.settings.teams ? "SQUADRE" : "TUTTI CONTRO TUTTI"} · ${arenaName}`;
      const columns = Math.max(1,Math.min(4,players.length));
      this.roster.style.setProperty("--intro-cols",String(columns));
      this.roster.style.setProperty("--intro-rows",String(Math.ceil(players.length/columns)));
      this.roster.replaceChildren(...players.map(([id,p],index)=>{
        const champion = findCharacter(p.characterId);
        const card = document.createElement("article");
        card.className = "intro-fighter";
        card.classList.toggle("self",id === localId);
        card.style.setProperty("--fighter-color",fighterCss(p.color,p.team));
        card.style.setProperty("--intro-delay",`${index*65}ms`);
        const tags = document.createElement("div");
        tags.className = "intro-tags";
        tags.textContent = [id === localId ? "TU" : p.bot ? "BOT" : "GIOCATORE",TEAM_NAMES[p.team],!p.connected ? "IN RICONNESSIONE" : ""].filter(Boolean).join(" · ");
        const canvas = document.createElement("canvas");
        canvas.width = canvas.height = 128;
        canvas.setAttribute("role","img");
        canvas.setAttribute("aria-label",champion?.displayName ?? p.characterId);
        const context = canvas.getContext("2d");
        if (context) void portrait(p.characterId).then(image=>{
          if (!canvas.isConnected) return;
          context.imageSmoothingEnabled = false;
          const scale = Math.min(canvas.width/image.naturalWidth,canvas.height/image.naturalHeight);
          const width = image.naturalWidth*scale;
          const height = image.naturalHeight*scale;
          context.clearRect(0,0,canvas.width,canvas.height);
          context.drawImage(image,(canvas.width-width)/2,(canvas.height-height)/2,width,height);
        }).catch(()=>{
          context.font = "bold 64px system-ui";
          context.fillStyle = "#f2c14e";
          context.textAlign = "center";
          context.fillText("?",64,90);
        });
        const name = document.createElement("h3");
        name.textContent = p.name;
        name.title = p.name;
        const character = document.createElement("p");
        character.className = "intro-champion";
        character.textContent = champion?.displayName ?? p.characterId;
        card.append(tags,canvas,name,character);
        return card;
      }));
    }
    this.remaining.textContent = "Preparati al combattimento";
    this.progress.style.width = `${Math.min(100,100*state.timeLeft/(MATCH_INTRO_TICKS/TICK_RATE))}%`;
  }
}
